import styled from "styled-components/native";
import React from "react";
import { Button, Spacer, VDBText, Input } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputPopupProps } from "./types";
import { schema } from "./utils/formSchema";
import { useTranslation } from "react-i18next";
import Modal from "@components/atoms/Modal";
import LineViewContainer from "@scenes/requests/utils/LineView";

const InputPopup = ({
  title,
  description,
  buttons,
  notes,
  showInput,
  inputText,
  isLoading,
  isForEditSavedSearch,
  showPopUpDialog,
  setShowPopUpDialog,
  modalType,
}: InputPopupProps) => {
  const { t } = useTranslation();

  const typography = useTypographyStore();
  const [enableButton, setEnableButton] = React.useState(true);

  const theme = useTheme();
  const { control, getValues } = useForm({
    resolver: yupResolver(schema),
  });

  const checkEnableButton = (titleText: string) => {
    setEnableButton(!!titleText);
  };

  return (
    <Modal
      isOpen={!!showPopUpDialog}
      onClose={() => {
        setShowPopUpDialog?.(false);
      }}
      headerTitle={title}
      hideHeader
      type={modalType}
    >
      <PopupHeader>
        <VDBText color={theme["core-05"]} type={typography["H1-B"]}>
          {title}
        </VDBText>
        <Spacer size={SpaceType.Basic} />
        {description && (
          <VDBText color={theme["core-06"]} type={typography["H2-R"]}>
            {description}
          </VDBText>
        )}
      </PopupHeader>
      {isForEditSavedSearch && (
        <NotesInputContainer isForEditSavedSearch={isForEditSavedSearch}>
          <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
            {notes}
          </VDBText>
        </NotesInputContainer>
      )}
      {showInput && (
        <InputContainer isForEditSavedSearch={isForEditSavedSearch}>
          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                inputLabel={t("savedSearch.popUp.placeholderText")}
                leftIcon={undefined}
                hideIconContainer={true}
                placeholder={t("savedSearch.popUp.placeholderText")}
                onBlur={onBlur}
                onChangeText={(text) => {
                  const alphanumericText = text.replace(
                    /^[ ]+|[^a-zA-Z0-9 ]/g,
                    ""
                  );
                  onChange(alphanumericText);
                  checkEnableButton(alphanumericText);
                }}
                value={value}
                error={undefined}
                defaultValue={inputText.toString()}
              />
            )}
            name="customTitle"
          />
        </InputContainer>
      )}
      {!isForEditSavedSearch && (
        <InputContainer isForEditSavedSearch={isForEditSavedSearch}>
          <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
            {notes}
          </VDBText>
        </InputContainer>
      )}
      <LineViewContainer background="core-04" />
      <Controls>
        {buttons.map((btn, index) => (
          <React.Fragment key={index}>
            <Button
              type={btn.type as ButtonType}
              loading={btn.isLoadingButton ? isLoading : false}
              category={ButtonCategory.default}
              disabled={btn.isLoadingButton ? !enableButton : false}
              title={btn.title}
              onPress={() =>
                btn.onPressAction({ title: getValues("customTitle") })
              }
            />
            {index !== buttons.length - 1 && <Spacer size={SpaceType.Double} />}
          </React.Fragment>
        ))}
      </Controls>
    </Modal>
  );
};

const PopupHeader = styled.View<any>`
  flex-direction: column;
  width: 100%;
  padding: 16px;
`;

const Controls = styled.View`
  padding: 24px 16px;
  flex-direction: column;
  width: 100%;
`;

const NotesInputContainer = styled.View<{ isForEditSavedSearch: boolean }>`
  display: flex;
  padding: 16px;
  padding-top: ${({ isForEditSavedSearch }) =>
    isForEditSavedSearch ? "24px" : "0px"};
  padding-bottom: ${({ isForEditSavedSearch }) =>
    isForEditSavedSearch ? "0px" : "0px"};
  flex-direction: column;
`;

const InputContainer = styled.View<{ isForEditSavedSearch: boolean }>`
  display: flex;
  padding: 16px;
  padding-bottom: ${({ isForEditSavedSearch }) =>
    isForEditSavedSearch ? "24px" : "0px"};
  flex-direction: column;
`;

export default InputPopup;
